<template>
  <HgBreadcrumb
    v-if="linksArray.length > 0"
    data-testid="breadCrumbs"
    :theme="getCurrentPlatformTheme()"
    :links="linksArray"
  />
</template>
<script setup lang="ts">
import { HgBreadcrumb, type HgBreadcrumbModel, HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { fetchGeoLocationsByIds, getGeoLocationById } from '@/api/geoService';
import { getDistinguishedDisplayValue } from '@/helpers/geo/geoLocationHelper';
import {
  cityLocale,
  municipalityLocale,
  replaceGeoTagReg,
  zipLocale,
} from '@/helpers/geo/geoUrlHelper';
import { getCurrentPlatformTheme, isPlatformIs24 } from '@/helpers/platform';
import { getBasePathWithLocation } from '@/router';
import { CITY_INDICATOR_IN_GEO_ID, type UrlParams } from '@/stores/types';
import useGeoStore from '@/stores/useGeoStore';
import { GeoIdLevels } from '@/types';

const { locale, t } = useI18n();
const geoStore = useGeoStore();
const { searchLocation } = storeToRefs(geoStore);
let linksGeneration = 0;

const homeLink = (): HgBreadcrumbModel => {
  return {
    name: t('common.homePage'),
    link: `${import.meta.env.VUE_PLATFORM_BASE_URL}/${locale.value}`,
  };
};
const countryLink = (): HgBreadcrumbModel => {
  return {
    name: t('common.mapTitle'),
    link: `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(locale.value as HgSupportedLanguage)}`,
  };
};
const linksArray = ref<Array<HgBreadcrumbModel>>([homeLink(), countryLink()]);

const shouldIncludeInBreadcrumb = (geoId: string): boolean =>
  geoId.includes(GeoIdLevels.Canton) ||
  geoId.includes(GeoIdLevels.Municipality) ||
  geoId.includes(GeoIdLevels.Town);

const getUrlName = (location: GeoLocation | null, language: HgSupportedLanguage): string => {
  if (!location) {
    return '';
  }
  if (isPlatformIs24()) {
    return location.is24UrlNames ? location.is24UrlNames[language] : '';
  }
  return location.urlNames[language];
};

const generateUrlParams = (
  language: HgSupportedLanguage,
  geoUrlName?: string,
  parentGeoUrlName?: string,
  isMunicipality?: boolean,
): UrlParams => ({
  param:
    parentGeoUrlName ||
    (isMunicipality
      ? geoUrlName!.replace(municipalityLocale[language], cityLocale[language])
      : (geoUrlName ?? '')),
  zipCity: parentGeoUrlName ? geoUrlName!.replace(replaceGeoTagReg(zipLocale[language]), '') : '',
});

const buildBreadcrumb = (
  location: GeoLocation,
  parentLoc: GeoLocation | null,
  language: HgSupportedLanguage,
): HgBreadcrumbModel => {
  const isMunicipality = location.type === 'municipality';
  const urlParams = generateUrlParams(
    language,
    getUrlName(location, language),
    parentLoc ? getUrlName(parentLoc, language) : undefined,
    isMunicipality,
  );
  const locationName = getDistinguishedDisplayValue(location, language, {
    inclAllNames: true,
  });
  let tmpUrl = urlParams.param;
  if (location.type === 'ziparea') {
    tmpUrl += '?zipCity=' + urlParams.zipCity;
  }
  const geoUrl = `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(language, tmpUrl)}`;

  return {
    name: locationName,
    link: geoUrl,
  };
};

const resolveParentCityLocation = async (
  location: GeoLocation,
  locationById: Map<string, GeoLocation>,
  currentGeneration: number,
): Promise<GeoLocation | null> => {
  const parentGeoId = location.parentPaths?.[0]?.find((item) =>
    item.includes(CITY_INDICATOR_IN_GEO_ID),
  );
  if (!parentGeoId) {
    return null;
  }

  const cachedParent = locationById.get(parentGeoId);
  if (cachedParent) {
    return cachedParent;
  }

  const parentLoc = await getGeoLocationById(parentGeoId);
  if (!isCurrentGeneration(currentGeneration)) {
    return null;
  }

  return parentLoc;
};

const isCurrentGeneration = (generation: number): boolean => generation === linksGeneration;

const commitLinks = (links: HgBreadcrumbModel[], generation: number): void => {
  if (isCurrentGeneration(generation)) {
    linksArray.value = links;
  }
};

const collectBreadcrumbGeoIds = (location: GeoLocation): string[] => {
  const geoIds =
    location.parentPaths?.[0]?.slice().reverse().filter(shouldIncludeInBreadcrumb) ?? [];

  geoIds.push(location.id);
  return geoIds;
};

const buildLocationLinks = async (
  geoIds: string[],
  locationById: Map<string, GeoLocation>,
  language: HgSupportedLanguage,
  generation: number,
): Promise<HgBreadcrumbModel[] | null> => {
  const links: HgBreadcrumbModel[] = [];

  for (const geoId of geoIds) {
    const location = locationById.get(geoId);
    if (!location) {
      continue;
    }

    const parentLoc =
      location.type === 'ziparea'
        ? await resolveParentCityLocation(location, locationById, generation)
        : null;

    if (!isCurrentGeneration(generation)) {
      return null;
    }

    links.push(buildBreadcrumb(location, parentLoc, language));
  }

  return links;
};

const generateLinks = async (): Promise<void> => {
  const generation = ++linksGeneration;
  const language = locale.value as HgSupportedLanguage;
  const baseLinks: HgBreadcrumbModel[] = [homeLink(), countryLink()];
  const currentSearchLocation = searchLocation.value;

  if (!currentSearchLocation?.parentPaths?.[0]) {
    commitLinks(baseLinks, generation);
    return;
  }

  const geoIds = collectBreadcrumbGeoIds(currentSearchLocation);
  const locations = await fetchGeoLocationsByIds(geoIds);

  if (!isCurrentGeneration(generation)) {
    return;
  }

  const locationById = new Map(locations.map((location) => [location.id, location]));
  const locationLinks = await buildLocationLinks(geoIds, locationById, language, generation);

  if (locationLinks) {
    commitLinks([...baseLinks, ...locationLinks], generation);
  }
};

watch(
  searchLocation,
  () => {
    generateLinks();
  },
  { immediate: true },
);
watch(locale, () => {
  generateLinks();
});
</script>
